import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';
import { PropertyProvider } from './contexts/PropertyContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initErrorTelemetry } from './lib/analytics';
import { initSentry } from './lib/sentry';
import { maybeInitAnalyticsWithConsent } from './components/CookieBanner';
import { reportWebVitals } from './lib/reportWebVitals';
import { applyStoredOptOut } from './lib/sessionReplay';

initSentry();                       // production error tracking (no-op when VITE_SENTRY_DSN unset)
maybeInitAnalyticsWithConsent();    // PostHog only fires if the user has explicitly opted in (GDPR/ePrivacy).
applyStoredOptOut();                // honour per-user session-replay opt-out before any pageview fires
initErrorTelemetry();               // window.error + unhandledrejection — defensive wrappers no-op without PostHog
reportWebVitals();                  // CLS, FCP, INP, LCP, TTFB → PostHog web_vital events

// Register the service worker (production only — dev gets vite HMR instead).
// v2 SW does network-only for navigations + cache-first for hashed /assets/*.
// See public/sw.js for the strategy and the v1-blank-page postmortem.
//
// Recovery for users still on v1 (which caches a stale shell):
//   - controllerchange fires when the new SW takes control of the page.
//   - We force a one-time reload so the broken cached shell is replaced
//     with the live network HTML the v2 SW now lets through.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // Check for updates on every load so a broken-cached user gets
        // the new SW immediately rather than waiting up to 24h.
        reg.update().catch(() => {});
      })
      .catch((err) => {
        // Don't break the app if SW registration fails — progressive enhancement.
        console.warn('Service worker registration failed:', err);
      });

    let didReloadForSwUpdate = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (didReloadForSwUpdate) return;
      didReloadForSwUpdate = true;
      // The new SW just took control. Reload so any in-flight cached shell
      // is discarded and the page is rebuilt against the live network HTML.
      window.location.reload();
    });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <PropertyProvider>
            <App />
          </PropertyProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </StrictMode>,
);
